/**
 * Number formatting for chemistry results: significant figures and scientific notation.
 *
 * Chemistry answers are reported to a number of *significant figures*, not decimal
 * places, and they run from 10⁻⁸ mol/L to 10²³ particles. These helpers turn a number
 * into the text a lab report would carry — "0.4281", "2.58 × 10²³" — in the reader's
 * decimal mark, with trailing zeros kept when they are significant.
 */

import { parseSigFigNumber } from "@/lib/tool-kit/math/sig-figs";

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

/** An integer exponent in Unicode superscripts: −8 → "⁻⁸". */
export function superscriptInt(value: number): string {
  let out = value < 0 ? "⁻" : "";
  for (const digit of String(Math.abs(value))) {
    out += SUPERSCRIPT_DIGITS[Number(digit)];
  }
  return out;
}

/** Significant figures in a typed number, or null when it is not a number. */
export function sigFigsOf(raw: string): number | null {
  const parsed = parseSigFigNumber(raw);
  if ("error" in parsed || parsed.isZero) {
    return null;
  }
  return Math.max(1, parsed.sigFigs);
}

/** Fewest significant figures among the typed numbers; `fallback` when none is readable. */
export function fewestSigFigs(
  raws: readonly string[],
  fallback: number
): number {
  const counts = raws
    .map(sigFigsOf)
    .filter((count): count is number => count !== null);
  return counts.length === 0 ? fallback : Math.min(...counts);
}

const PLAIN_LOW = 1e-3;
const PLAIN_HIGH = 1e6;

/** `2.58 × 10²³`, with the mantissa in the reader's decimal mark. */
export function scientific(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  const [mantissa, exponent] = value.toExponential(sig - 1).split("e");
  const shown = fmt(Number(mantissa), {
    minimumFractionDigits: sig - 1,
    maximumFractionDigits: sig - 1,
  });
  return `${shown} × 10${superscriptInt(Number(exponent))}`;
}

/**
 * Round to `sig` significant figures and write it: plain for everyday magnitudes
 * (0.001 up to 999,999), scientific notation beyond. Zero is "0".
 */
export function formatSig(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const clamped = Math.min(Math.max(Math.round(sig), 1), 17);
  const magnitude = Math.abs(value);
  if (magnitude < PLAIN_LOW || magnitude >= PLAIN_HIGH) {
    return scientific(fmt, value, clamped);
  }
  return fmt(value, {
    minimumSignificantDigits: clamped,
    maximumSignificantDigits: clamped,
    useGrouping: false,
  });
}

/** A full-precision echo of an intermediate value without float noise (0.13, not 0.1300000007). */
export function formatPlain(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude < PLAIN_LOW || magnitude >= 1e9)) {
    return scientific(fmt, value, 5);
  }
  return fmt(value, { maximumSignificantDigits: 8, useGrouping: false });
}

/** Fixed decimal places: 18.015 g/mol. */
export function formatFixed(
  fmt: NumberFormatter,
  value: number,
  places: number
): string {
  return fmt(value, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
    useGrouping: false,
  });
}

/**
 * A value written with every digit it carries and no float noise, for conversion factors
 * and intermediate numbers that must not be rounded for display: 6.02214076 × 10²³ stays
 * 6.02214076 × 10²³, 1609.344 stays 1609.344.
 */
export function formatExact(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const magnitude = Math.abs(value);
  if (magnitude >= PLAIN_LOW && magnitude < 1e9) {
    return fmt(value, { maximumSignificantDigits: 12, useGrouping: false });
  }
  const [mantissa, exponent] = Number(value.toPrecision(12))
    .toExponential()
    .split("e");
  const digits = mantissa.replace("-", "").replace(".", "").length;
  return `${fmt(Number(mantissa), {
    minimumFractionDigits: digits - 1,
    maximumFractionDigits: digits - 1,
  })} × 10${superscriptInt(Number(exponent))}`;
}
